import type { UpdateOAuthApp } from '@wf-coe/types';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useApiClient } from '../lib/use-api-client.js';
import type { OAuthAppDto } from './use-oauth-apps.js';

interface UpdateOAuthAppInput {
  clientId: string;
  appId: string;
  patch: UpdateOAuthApp;
}

/**
 * Mutation hook for `PUT /v1/clients/{clientId}/oauth-apps/{appId}`.
 * Invalidates the per-client list query so the row's edits show up on
 * the OAuth Apps page without manual refetch.
 *
 * Casbin `(oauth-app, write)` (Admin / Operator).
 */
export function useUpdateOAuthAppMutation(): UseMutationResult<
  OAuthAppDto,
  Error,
  UpdateOAuthAppInput
> {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, appId, patch }: UpdateOAuthAppInput) => {
      if (!api) throw new Error('useUpdateOAuthAppMutation: api client unavailable');
      return api.put<OAuthAppDto>(`/v1/clients/${clientId}/oauth-apps/${appId}`, patch);
    },
    onSuccess: (_data, { clientId }) => {
      void queryClient.invalidateQueries({ queryKey: ['oauth-apps', clientId] });
    },
  });
}
